/**
 * ModelMetricsPage — one model's operational metrics (TRA-1461), reached
 * from the model page header: requests by status, latency and time to
 * first token percentiles, throughput, request size, prompt cache, errors
 * and, for a model on our dedicated fleet, replicas over time. One window
 * picker drives every card, and the arrows on any chart move every card to
 * earlier or later times by half the window (TRA-1592). The window
 * and the stretch live in the URL (`?hours=`, `?end=`) so a link opens on
 * the same one; no `end` is now. A drag across any chart zooms every chart
 * to that stretch (useMetricsZoom). Every chart carries the same controls,
 * like the model page's replicas chart: arrows on its edges, shown while
 * the pointer is over it, and Reset zoom at its top right while the page is
 * zoomed or moved, which goes back to now. A click on any chart's control
 * moves every chart. The page carries no hint or "Showing" line: the chart
 * axes say which stretch is shown.
 *
 * Every number comes from the gateway through one metrics read, which
 * takes the end of the stretch and reads only it. The capacity band reads
 * the same replica history the model page's "Replicas over time" band
 * does, which the gateway keeps for seven days, and the engine section
 * (TRA-1484) reads the gateway's vLLM samples for a model on our fleet;
 * both only take hours back from now, so moved back they load a longer
 * window and draw the stretch shown. A zoom reads nothing: every card cuts
 * what it has loaded. Once a zoom has held still, the main cards read just
 * that stretch at full detail in the background and swap to it.
 */

import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

import { getApiErrorStatus } from '@/api/client'
import { Note, type PagePan } from '@/components/models/home/PanControls'
import { MAX_METRICS_HOURS } from '@/components/models/home/scalingWindow'
import { useWindowBox } from '@/components/models/home/useWindowBox'
import {
  WindowBoxError,
  WindowPicker,
  type WindowOption,
} from '@/components/models/home/WindowPicker'
import { Capacity } from '@/components/models/metrics/Capacity'
import { Engine } from '@/components/models/metrics/Engine'
import { errorList, toMetricsView } from '@/components/models/metrics/contracts'
import { endFrom, paramsWithEnd, paramsWithHours } from '@/components/models/metrics/metricsPan'
import { sameRange, sharpParams } from '@/components/models/metrics/metricsZoom'
import { ModelMetricsPanel } from '@/components/models/metrics/ModelMetricsPanel'
import { useMetricsZoom } from '@/components/models/metrics/useMetricsZoom'
import { Button } from '@/components/ui/button'
import { PageContainer } from '@/components/ui/page-container'
import { Skeleton } from '@/components/ui/skeleton'
import { TEAM_SCOPE, useFallbackOptions, useModel } from '@/hooks/useModels'
import { useModelMetrics } from '@/hooks/useUsage'

const WINDOWS: WindowOption[] = [
  { value: '1', label: '1h' },
  { value: '6', label: '6h' },
  { value: '24', label: '24h' },
  { value: '72', label: '3d' },
  { value: '168', label: '7d' },
  { value: '720', label: '30d' },
]

const DEFAULT_HOURS = 24
const WINDOW_HELP = 'Enter 1h to 30d, for example 36h or 5d.'
const WINDOW_ERROR_ID = 'metrics-window-error'

/** The `?hours=` a link carries, or the default when it is missing or out of range. */
function hoursFrom(raw: string | null): number {
  const hours = Number(raw)
  return Number.isInteger(hours) && hours >= 1 && hours <= MAX_METRICS_HOURS ? hours : DEFAULT_HOURS
}

export default function ModelMetricsPage(): React.JSX.Element {
  const { modelId } = useParams<{ modelId: string }>()
  const { data: model, isLoading, isError, error } = useModel(modelId)
  const [searchParams, setSearchParams] = useSearchParams()
  const hours = hoursFrom(searchParams.get('hours'))
  const end = endFrom(searchParams.get('end'), hours)

  function setEnd(next: number | null): void {
    setSearchParams((prev) => paramsWithEnd(prev, next), { replace: true })
  }

  const { zoom, settled, shown, view, replicaView, setZoom, resetZoom, step, backToNow } =
    useMetricsZoom(hours, end, setEnd)

  function setHours(next: number): void {
    resetZoom()
    setSearchParams((prev) => paramsWithHours(prev, next), { replace: true })
  }

  // Every chart carries the same arrows and Reset zoom (TRA-1592), so a
  // click on any chart moves all of them.
  const pan: PagePan = {
    canEarlier: view.canEarlier,
    canLater: view.canLater,
    earlier: () => {
      step('earlier')
    },
    later: () => {
      step('later')
    },
    showReset: shown !== null,
    reset: backToNow,
  }

  const box = useWindowBox(MAX_METRICS_HOURS, setHours)
  const metrics = useModelMetrics(modelId, hours, end)
  // The zoomed stretch at full detail, read once the zoom holds still. A
  // placeholder here is the previous zoom's read, not this one's.
  const sharp = settled === null ? null : sharpParams(settled)
  const sharper = useModelMetrics(sharp === null ? undefined : modelId, sharp?.hours ?? 1, sharp?.end ?? null)
  const sharpData = sameRange(settled, zoom) && !sharper.isPlaceholderData ? sharper.data : undefined
  const sharpFailed = sameRange(settled, zoom) && sharper.isError
  const fallbackOptions = useFallbackOptions(modelId, TEAM_SCOPE)

  if (isLoading) {
    return (
      <PageContainer className="space-y-6">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    )
  }

  // A 404 means the model is not in the active team (team switch) or was
  // deleted: go to the section root, not a dead error page.
  if (isError && getApiErrorStatus(error) === 404) {
    return <Navigate to="/models" replace />
  }

  if (isError || !model) {
    return (
      <PageContainer>
        <p className="text-sm text-destructive">Failed to load model.</p>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <div className="border-b border-border pb-6">
        <Button variant="ghost" size="sm" asChild className="-ml-2 mb-3">
          <Link to={`/models/${model.id}`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {model.display_name}
          </Link>
        </Button>
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <h1 className="font-serif text-2xl font-medium">Metrics</h1>
            <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {model.gateway_model_id}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <WindowPicker
              options={WINDOWS}
              hours={hours}
              onChange={setHours}
              box={box}
              pillsLabel="Metrics window"
              boxLabel="Custom metrics window"
              errorId={WINDOW_ERROR_ID}
            />
          </div>
        </div>
        <WindowBoxError box={box} id={WINDOW_ERROR_ID} help={WINDOW_HELP} />
        {/* The previous window stays up while the next one loads. */}
        {metrics.isPlaceholderData && <Note>Loading...</Note>}
        {zoom !== null && sharpData === undefined && sharper.isFetching && <Note>Loading more detail...</Note>}
        {zoom !== null && sharpFailed && <Note>Could not load more detail for this stretch.</Note>}
      </div>

      {metrics.isError ? (
        <p className="py-6 text-sm text-destructive">Failed to load gateway metrics.</p>
      ) : metrics.data === undefined ? (
        <div className="space-y-6 py-6">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-44 w-full" />
        </div>
      ) : (
        // Zoomed, the cards draw the loaded window cut to the zoom until
        // the zoom's own read lands; the error list waits for that read.
        <ModelMetricsPanel
          view={toMetricsView(sharpData ?? metrics.data, zoom)}
          {...errorList(metrics.data, zoom, sharpData, sharpFailed)}
          onZoom={setZoom}
          pan={pan}
          capacity={
            <Capacity
              modelId={model.id}
              hours={hours}
              end={end}
              view={replicaView}
              pan={pan}
              isBaseten={fallbackOptions.data?.forwards_to.upstream_id === 'baseten'}
              routeLoaded={fallbackOptions.data !== undefined || fallbackOptions.isError}
              routeFailed={fallbackOptions.isError}
            />
          }
        />
      )}
      <Engine
        modelId={model.id}
        hours={hours}
        end={end}
        shown={view.span}
        onZoom={setZoom}
        pan={pan}
        isBaseten={fallbackOptions.data?.forwards_to.upstream_id === 'baseten'}
        routeLoaded={fallbackOptions.data !== undefined || fallbackOptions.isError}
        routeFailed={fallbackOptions.isError}
      />
    </PageContainer>
  )
}
